import { WIDGET_TYPE } from 'business/constants';
import { PageSeparator } from 'business/definitions';
import _ from 'lodash';
import { WidgetT } from 'routes/Dashboard/components/WidgetsLayout/helpers';
import {
    NB_RGL_UNIT_ALLOWED_FIRST_PAGE,
    NB_RGL_UNIT_PER_PRINT_PAGE,
    ROW_HEIGHT_FULL_WIDTH,
    ROW_HEIGHT_FIXED_WIDTH,
} from 'routes/Dashboard/components/WidgetsLayout/PrintableGrid/constants';
import { PrintableWidget } from 'routes/Dashboard/components/WidgetsLayout/PrintableGrid/helpers/paginationHelpers';

export const getRowHeight = (isFullWidth: boolean) =>
    isFullWidth ? ROW_HEIGHT_FULL_WIDTH : ROW_HEIGHT_FIXED_WIDTH;

const getFirstPageHeight = (isFullWidth: boolean) =>
    getRowHeight(isFullWidth) * NB_RGL_UNIT_ALLOWED_FIRST_PAGE; // pixels

const getPageHeight = (isFullWidth: boolean) =>
    getRowHeight(isFullWidth) * NB_RGL_UNIT_PER_PRINT_PAGE; // pixels

const getNbPages = (maxYPlusH: number, isFullWidth: boolean): number => {
    const nbPixels = getRowHeight(isFullWidth) * maxYPlusH;
    if (nbPixels < getFirstPageHeight(isFullWidth)) {
        return 1;
    }
    return (
        1 +
        Math.ceil(
            (nbPixels - getFirstPageHeight(isFullWidth)) /
                getPageHeight(isFullWidth),
        )
    );
};

export const getPageSeparatorsYPos = (
    widgets: WidgetT[],
    isFullWidth: boolean,
): number[] => {
    // max of {widget.y + widget.h, for widget in widgets}. Useful for PDF pages separators.
    const maxYPlusH = Math.max(
        ...widgets.map((w) => (w.position.md.y || 0) + w.position.md.h),
    );
    // NOTE: in some cases, when we have more than 1 pageBreak widgets, and we are resizing an element,
    // the height of one of the pageBreaks is not updated causing it to be on the same page as the other
    // so we need the number of pages to be at least equal to the number of page breaks to avoid this issue.
    const nbPages = Math.max(
        getNbPages(maxYPlusH, isFullWidth),
        widgets.filter((w) => w.type === WIDGET_TYPE.pagebreak).length,
    );
    return _.map(
        Array(nbPages),
        (value, index) =>
            getFirstPageHeight(isFullWidth) +
            index * getPageHeight(isFullWidth),
    );
};

/**
 * For a given widget and the vertical position of page separators
 * returns the number of React-Grid-Layout rows that are overflowing
 * from the current page of that widget
 */
export const getWidgetPageOverflowRows = (
    widget: WidgetT | PrintableWidget,
    separatorsYPos: number[],
    isFullWidth: boolean,
): number => {
    const widgetFirstRowPos = widget.position.md?.y || 0;
    const widgetLastRowPos = widgetFirstRowPos + (widget.position.md?.h || 0);
    return separatorsYPos.reduce((widgetOverflow, y) => {
        const separatorRowNumber = y / getRowHeight(isFullWidth);
        if (
            _.inRange(
                separatorRowNumber,
                widgetFirstRowPos + 1,
                widgetLastRowPos,
            )
        ) {
            return widgetLastRowPos - separatorRowNumber;
        }
        return widgetOverflow;
    }, 0);
};

export const isWidgetTruncated = (widget: WidgetT | PrintableWidget): boolean =>
    Boolean(widget?.pageRowOverflow);

export const isSeparatorCuttingWidget = (
    separatorYPos: number,
    widgets: WidgetT[],
    isFullWidth: boolean,
): boolean =>
    widgets.some((widget) => {
        const widgetFirstRowPos = widget.position.md?.y || 0;
        const widgetLastRowPos =
            widgetFirstRowPos + (widget.position.md?.h || 0);
        return _.inRange(
            separatorYPos / getRowHeight(isFullWidth),
            widgetFirstRowPos + 1,
            widgetLastRowPos,
        );
    });

/**
 * Gets the list of pageSeparators positions, and widgets
 * with number of overflowing rows if widget is truncated (0 else)
 */
export const getPageSeparatorsAndWidgetsOverflow = (
    widgets: WidgetT[],
    isFullWidth: boolean = true,
): [WidgetT[], PageSeparator[]] => {
    const separatorsYPos = getPageSeparatorsYPos(widgets, isFullWidth);
    const widgetsWithPageOverflowRows: WidgetT[] = widgets.map((widget) => ({
        ...widget,
        pageRowOverflow: getWidgetPageOverflowRows(
            widget,
            separatorsYPos,
            isFullWidth,
        ),
    }));
    const pageSeparators = separatorsYPos.map((yPos) => ({
        yPos,
        isCuttingWidget: isSeparatorCuttingWidget(yPos, widgets, isFullWidth),
    }));
    return [widgetsWithPageOverflowRows, pageSeparators];
};

/**
 *
 * @param widgets Calculate the height of the dashboard based on the widgets
 * @returns the height of the dashboard
 */
export const getDashboardHeight = (widgets: WidgetT[]): number => {
    return (
        widgets.reduce(
            (a, b) => Math.max(a, b.position.md.y + b.position.md.h),
            -Infinity,
        ) *
            (40 + 10) +
        10
    );
};
